/**
 * AppointmentBlock
 * Absolutely positioned block inside a day column representing a single appointment.
 */
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Link } from "@tanstack/react-router";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@jourfx/ui";

export interface CalendarAppointment {
  _id: string;
  scheduledAt: number;
  duration: number;
  status: string;
  versicherterName?: string | null;
  gutachterName?: string | null;
  templateColor?: string | null;
}

interface AppointmentBlockProps {
  appointment: CalendarAppointment;
  startHour: number;
  endHour: number;
}

const STATUS_LABELS: Record<string, string> = {
  scheduled: "Geplant",
  confirmed: "Bestätigt",
  in_progress: "Laufend",
  completed: "Abgeschlossen",
  cancelled: "Storniert",
  no_show: "Nicht erschienen",
};

export function AppointmentBlock({
  appointment,
  startHour,
  endHour,
}: AppointmentBlockProps) {
  const start = new Date(appointment.scheduledAt);
  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const gridStart = startHour * 60;
  const gridEnd = endHour * 60;
  const gridRange = gridEnd - gridStart;

  const topPct = ((startMinutes - gridStart) / gridRange) * 100;
  const heightPct = (appointment.duration / gridRange) * 100;

  const bg = appointment.templateColor || "#3b82f6";
  const timeLabel = format(start, "HH:mm", { locale: de });
  const versicherterName = appointment.versicherterName || "Versicherter";
  const statusLabel = STATUS_LABELS[appointment.status] || appointment.status;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to="/appointments/$appointmentId"
          params={{ appointmentId: appointment._id }}
          className="absolute inset-x-1 z-10 overflow-hidden rounded-md px-2 py-1 text-xs text-white shadow-sm transition-opacity hover:opacity-90"
          style={{
            top: `${topPct}%`,
            height: `${heightPct}%`,
            minHeight: "1.5rem",
            backgroundColor: bg,
          }}
        >
          <p className="truncate font-medium">{versicherterName}</p>
          <p className="truncate opacity-80">
            {timeLabel} &middot; {appointment.duration}&nbsp;Min.
          </p>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs text-sm">
        <p className="font-medium">{versicherterName}</p>
        <p>
          {timeLabel} Uhr &middot; {appointment.duration}&nbsp;Minuten
        </p>
        {appointment.gutachterName && (
          <p className="text-muted-foreground">
            Gutachter: {appointment.gutachterName}
          </p>
        )}
        <p className="text-muted-foreground">Status: {statusLabel}</p>
      </TooltipContent>
    </Tooltip>
  );
}
